// Ch94 · SOCIALS COUNTS — the one place a count decides how to say itself.
//
// PKT-0792 binding rule, verbatim:
//
//   "Counts render a NUMBER or '—', never a fabricated 0.
//    Unknown, unavailable and zero must look different."
//
// Three states, three appearances. The rule exists because the failure it
// prevents is silent: a `?? 0` at a call site turns "we could not read this"
// into "there are none", and a screen confidently reporting zero work is
// indistinguishable from a screen reporting a working system with no work.
// Grant acts on these numbers. A fabricated zero tells him to stop looking.
//
//   COUNTED      we read it. `0` is a real answer and renders "0".
//   UNKNOWN      we asked and got nothing back. Renders "—".
//   UNAVAILABLE  there is no source yet — the thing is not built or not
//                connected. Renders in WORDS, because a glyph would read as
//                "unknown" and this is a different, permanent-until-fixed fact.
//
// Same discipline as leadsCalendar's "free text like 'August' never becomes a
// date": we do not manufacture a value to fill a slot.
//
// No React here on purpose, so every rule below is testable without a DOM.

/** A count that knows what it does and does not know. */
export type SocialsCount =
  | { kind: "counted"; value: number }
  | { kind: "unknown" }
  | { kind: "unavailable"; reason: string };

/** A real reading. `0` is a legitimate value and must reach the screen as "0". */
export const counted = (value: number): SocialsCount => ({ kind: "counted", value });

/** We asked; the answer did not arrive. Transient. */
export const unknown = (): SocialsCount => ({ kind: "unknown" });

/**
 * There is no source for this number yet.
 *
 * `reason` is shown to Grant, so it is written in his words — "Instagram isn't
 * connected yet", never "no roster run" or "edge is UNKNOWN".
 */
export const unavailable = (reason: string): SocialsCount => ({
  kind: "unavailable",
  reason,
});

/**
 * Adapt a value the API may omit.
 *
 * 🔴 DELIBERATELY CONSERVATIVE. `null` and an absent key are BOTH treated as
 * UNKNOWN here, and that is a floor, not a claim that they mean the same thing.
 * The content seat is measuring the real per-field semantics (some fields are
 * "absent, not empty" ON PURPOSE, and for those absent carries information).
 * When that contract lands, fields that distinguish the two get their own
 * adapter — they do not get this one loosened.
 *
 * What this function will never do is turn a missing value into `0`.
 */
export const fromNullable = (value: number | null | undefined): SocialsCount =>
  typeof value === "number" && Number.isFinite(value) ? counted(value) : unknown();

/** What the count looks like, and what it says when you hover it. */
export interface CountDisplay {
  /** The glyph or word shown in the chip, pill or tile. */
  text: string;
  /** Hover/aria explanation. `null` when the text needs no explaining. */
  title: string | null;
  /**
   * True only for a real reading. Callers use this to decide whether a number
   * may be summed, compared, or used to drive a chart — never to decide colour.
   */
  isNumber: boolean;
}

export function displayCount(count: SocialsCount): CountDisplay {
  switch (count.kind) {
    case "counted":
      return { text: String(count.value), title: null, isNumber: true };
    case "unknown":
      return {
        text: "—",
        title: "We couldn't read this just now.",
        isNumber: false,
      };
    case "unavailable":
      return { text: count.reason, title: null, isNumber: false };
  }
}

/**
 * Add counts without laundering an unknown into a total.
 *
 * A sum containing an unknown is UNKNOWN, not the sum of the parts it happens
 * to have. Reporting "12" when one of three inputs never answered is the
 * fabricated-zero bug wearing a different hat.
 *
 * An UNAVAILABLE input is skipped rather than poisoning the total: "Instagram
 * isn't connected" is a known absence of a source, not a gap in a reading, so
 * Reddit's 12 remains an honest 12. If every input is unavailable the total is
 * unavailable, carrying the first reason.
 */
export function sumCounts(counts: SocialsCount[]): SocialsCount {
  if (counts.some((c) => c.kind === "unknown")) return unknown();

  const readings = counts.filter(
    (c): c is Extract<SocialsCount, { kind: "counted" }> => c.kind === "counted"
  );
  if (readings.length === 0) {
    const firstUnavailable = counts.find(
      (c): c is Extract<SocialsCount, { kind: "unavailable" }> =>
        c.kind === "unavailable"
    );
    return firstUnavailable ? unavailable(firstUnavailable.reason) : counted(0);
  }
  return counted(readings.reduce((total, c) => total + c.value, 0));
}

/**
 * The filter strip's "N of M" line.
 *
 * Both halves must be real readings. If we do not know the total, we do not
 * claim a fraction of it — a confident "6 of 6" on a queue of 29 is worse than
 * saying nothing, because it reads as "you are done".
 */
export function showingOf(shown: SocialsCount, total: SocialsCount): string | null {
  if (shown.kind !== "counted" || total.kind !== "counted") return null;
  return `Showing ${shown.value} of ${total.value}`;
}
